import * as React from "react"
import type { Project } from "../lib/client"
import { languageName, type Category, type DocEntry } from "../lib/docs"
import { childrenOf, laneNodes, type Node } from "../lib/tree"
import { Icon } from "./Icon"
import { Profile } from "./Profile"

/**
 * The menu: categories, and what is directly inside one.
 *
 * **The arrow and the name do different things**, which is the whole shape of this. The
 * arrow opens the category *in place* so you can see what is in it without leaving where
 * you are; the name opens the category's own page. A menu where those are the same click
 * forces a navigation to answer "what is in there", and that is the question a menu exists
 * to answer.
 *
 * Expanding is recursive and costs nothing over the relay: the whole index arrived in one
 * frame and the tree is derived from it here.
 */
export const Sidebar: React.FC<{
  repo: string
  projects: Project[]
  project: string
  categories: Category[]
  entries: DocEntry[]
  languages: string[]
  lang: string
  current: string
  clientOnline: boolean | null
  who: string
  lanes: Array<{ id: string; label: string; hidden: boolean }>
  onCollapse: () => void
  onProject: (id: string) => void
  onCategory: (id: string) => void
  onEnter: (dir: string) => void
  onOpen: (path: string) => void
  onLang: (code: string) => void
  onHome: () => void
  onSignOut: () => void
}> = ({ repo, projects, project, categories, entries, languages, lang, current,
        clientOnline, who, lanes, onCollapse, onProject, onCategory, onEnter, onOpen, onLang,
        onHome, onSignOut }) => {
  const [open, setOpen] = React.useState<Record<string, boolean>>({})
  const toggle = (id: string) => setOpen(o => ({ ...o, [id]: !o[id] }))

  return (
    <nav className="sf-sidebar-inner" aria-label="Categories">
      <div className="sf-brand-row">
        <button className="sf-brand" onClick={onHome}>
          <div className="sf-brand-mark">FS</div>
          <div className="sf-brand-text">
            <b>Faion Suite</b>
            <span>{repo.split("/").pop()}</span>
          </div>
        </button>
        {/* Collapsing hides the menu rather than shrinking it to a rail of icons: these are
            the repository's own section names, and an icon cannot stand for a name nobody
            chose from a fixed set. */}
        <button className="sf-collapse" onClick={onCollapse}
                aria-label="Hide the menu" title="Hide the menu">«</button>
      </div>

      {projects.length > 1 && (
        <label className="sf-field sf-project">
          <span>project</span>
          <select value={project} onChange={e => onProject(e.target.value)}>
            {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </label>
      )}

      {/* How many languages a repository is kept in is its own decision (`.agent-languages`),
          so the control has to survive any number of them. One: nothing to switch, and a
          switch with a single option is furniture. Two: both visible, one click to flip.
          More: a row of buttons stops fitting a sidebar and starts wrapping, so a select. */}
      {languages.length === 2 && (
        <div className="sf-lang" role="group" aria-label="Language">
          {languages.map(code => (
            <button key={code} className={`sf-lang-opt ${code === lang ? "is-active" : ""}`}
                    onClick={() => onLang(code)}>{languageName(code)}</button>
          ))}
        </div>
      )}
      {languages.length > 2 && (
        <label className="sf-field sf-project">
          <span>language</span>
          <select value={lang} onChange={e => onLang(e.target.value)}>
            {languages.map(code => (
              <option key={code} value={code}>{languageName(code)}</option>
            ))}
          </select>
        </label>
      )}

      <div className="sf-nav">
        {categories.map(c => (
          <div key={c.id} className="sf-nav-group">
            <div className={`sf-link ${c.id === current ? "is-active" : ""}`}>
              <button className="sf-twist" aria-expanded={!!open[c.id]}
                      aria-label={`Show what is in ${c.label}`}
                      onClick={() => toggle(c.id)}>{open[c.id] ? "▾" : "▸"}</button>
              <button className="sf-link-name" onClick={() => onCategory(c.id)}>
                {c.label}
              </button>
              <span className="sf-count">{c.count || "—"}</span>
            </div>
            {open[c.id] && (
              <Branch nodes={laneNodes(entries, c.id, new Set(lanes.map(l => l.id)))}
                      entries={entries} depth={1}
                      current={current} onEnter={onEnter} onOpen={onOpen} />
            )}
          </div>
        ))}
      </div>

      <div className="sf-side-foot">
        <span className="sf-presence">
          <span className={`sf-dot ${clientOnline ? "on" : "off"}`} />{" "}
          {clientOnline ? "Client connected" : "Client offline"}
        </span>
        <Profile who={who} repo={repo} languages={languages} lanes={lanes}
                 onSignOut={onSignOut} />
      </div>
    </nav>
  )
}

/** One level, and its own levels underneath. A board does not open in the menu — it is a
 * page of columns, and a column list in a sidebar is neither. */
const Branch: React.FC<{
  nodes: Node[]
  entries: DocEntry[]
  depth: number
  current: string
  onEnter: (dir: string) => void
  onOpen: (path: string) => void
}> = ({ nodes, entries, depth, current, onEnter, onOpen }) => {
  const [open, setOpen] = React.useState<Record<string, boolean>>({})
  if (!nodes.length) return <p className="sf-branch-empty">nothing in here yet</p>
  return (
    <div className="sf-branch" style={{ paddingLeft: `${depth * 0.75}rem` }}>
      {nodes.map(n => (
        <div key={n.path}>
          <div className={`sf-sublink ${n.path === current ? "is-active" : ""}`}>
            {n.dir && n.kind !== "board" ? (
              <button className="sf-twist" aria-expanded={!!open[n.path]}
                      aria-label={`Show what is in ${n.title}`}
                      onClick={() => setOpen(o => ({ ...o, [n.path]: !o[n.path] }))}>
                {open[n.path] ? "▾" : "▸"}
              </button>
            ) : (
              <span className="sf-twist is-leaf">
                <Icon kind={n.kind} />
              </span>
            )}
            <button className="sf-link-name"
                    onClick={() => (n.dir ? onEnter(n.path) : onOpen(n.path))}>
              {n.title}
            </button>
          </div>
          {open[n.path] && (
            <Branch nodes={childrenOf(entries, n.path)} entries={entries} depth={depth + 1}
                    current={current} onEnter={onEnter} onOpen={onOpen} />
          )}
        </div>
      ))}
    </div>
  )
}
